import { useNavigation } from '@react-navigation/native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import {
  Button,
  ButtonsLayout,
  ButtonStyleEnum,
  Field,
  Form,
  FormButton,
  Loader,
  MultilineField,
  Separator,
  SwitchField,
  FormRender,
} from '@components';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ProfileNavigatorNavigationPropType } from '@navigation';
import { ToastService } from '@services';
import { IS_ANDROID, IS_MOBILE, IS_WEB, parseApiError } from '@utils';
import {
  profileGeneralSchema,
  ProfileGeneralSchemaDataType,
} from '@validation';

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const LoaderContainer = styled(View)({
  flex: 1,
  justifyContent: 'center',
});

const FormContainer = styled(View)({
  maxWidth: ['100%', 600],
  flexGrow: 1,
});

const InnerMobileContainer = styled(View)({
  flex: 1,
  justifyContent: 'space-between',
});

const LocationFieldContainer = styled(FormContainer)({
  mr: IS_ANDROID ? '$2' : '$6',
});

const FormInnerContainer = IS_MOBILE
  ? InnerMobileContainer
  : Fragment;

const cancelButtonStyle = IS_WEB
  ? ButtonStyleEnum.Outline
  : ButtonStyleEnum.Ghost;

export interface IProfileFormGeneralProps {
  hideForm?: () => void;
}

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'UpdateProfileGeneral'>;

export const ProfileFormGeneral = observer<IProfileFormGeneralProps>(
  (props: IProfileFormGeneralProps) => {
    const { t } = useTranslation();
    const nav = useNavigation<NavigationPropType>();

    const viewer = useViewerModel();
    const user = viewer.currentUser;

    async function submitForm(values: ProfileGeneralSchemaDataType) {
      try {
        await viewer.update.run({
          firstName: values.firstName,
          lastName: values.lastName,
          location: values.location,
          description: values.description,
          settings: {
            hideLocation: !values.locationShown,
          },
        });

        closeForm();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function closeForm() {
      if (IS_WEB && props.hideForm) {
        props.hideForm();
        return;
      }
      nav.goBack();
    }

    function showToast(_: any, locationShown: boolean) {
      // TODO: Fix Toast position on mobile
      ToastService.showSuccess(
        t('profile.locationSwitch', {
          context: locationShown ? 'on' : 'off',
        }),
      );
    }

    if (!user) {
      return (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      );
    }

    const initialValues: ProfileGeneralSchemaDataType = {
      firstName: user.firstName,
      lastName: user.lastName,
      location: user.location,
      description: user.description,
      locationShown: !user.settings.hideLocation,
    };

    return (
      <Form<ProfileGeneralSchemaDataType>
        onSubmit={submitForm}
        initialValues={initialValues}
        validationSchema={profileGeneralSchema}
        validateOnMount
      >
        <FormInnerContainer>
          <View>
            <FormContainer>
              <Separator />
              <Field
                name="firstName"
                placeholder={t('profile.firstName')}
              />

              <Separator />
              <Field
                name="lastName"
                placeholder={t('profile.lastName')}
              />
            </FormContainer>

            <Separator />
            <RowContainer>
              <LocationFieldContainer>
                <FormRender<ProfileGeneralSchemaDataType>>
                  {(formik) => (
                    <Field
                      name="location"
                      placeholder={t('profile.location')}
                      disabled={!formik.values.locationShown}
                    />
                  )}
                </FormRender>
              </LocationFieldContainer>

              <SwitchField
                name="locationShown"
                onValueChange={showToast}
              />
            </RowContainer>

            <FormContainer>
              <Separator />
              <MultilineField
                name="description"
                placeholder={t('profile.description')}
              />
              <Separator />
            </FormContainer>
          </View>

          <ButtonsLayout>
            <FormButton>{t('profile.save')}</FormButton>
            <Button
              buttonStyle={cancelButtonStyle}
              onPress={closeForm}
            >
              {t('profile.cancel')}
            </Button>
          </ButtonsLayout>
        </FormInnerContainer>
      </Form>
    );
  },
);
